'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import {
  cancelTomVendingPurchaseOrder,
  createTomVendingPurchaseOrder,
  acceptTomVendingPolicyProposal,
  dismissTomVendingPolicyProposal,
  fetchTomVendingInventorySettings,
  fetchTomVendingPolicyProposals,
  queryTomVendingBuyingSuggestions,
  queryTomVendingReplenishmentPolicies,
  saveTomVendingReplenishmentPolicy,
  searchTomVendingAlertUsers,
  updateTomVendingInventorySettings,
  updateTomVendingPurchaseOrder,
  useTomVendingPurchaseOrdersList,
  useTomVendingVendorsList,
  type TvPurchaseOrder,
} from '@/hooks/useTomVendingPurchasing';
import { tomVendingReceiveAtLocations, useTomVendingLocationsList } from '@/hooks/useTomVendingLocation';
import { useNotification } from '@/contexts/NotificationContext';
import BuyingSuggestionsPanel, { type BuyingSuggestionRow } from '../purchasing/BuyingSuggestionsPanel';
import ReplenishmentPolicyPanel, { type ReplenishmentPolicyRow } from '../purchasing/ReplenishmentPolicyPanel';
import { searchTomVendingCatalogItems } from '../purchasing/catalogSearch';
import TvPolicyProposalsPanel, { type TvPolicyProposal } from '../purchasing/TvPolicyProposalsPanel';
import InventoryAlertSettingsCard from '../purchasing/InventoryAlertSettingsCard';

function statusChip(status: string) {
  const map: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
    draft: 'default',
    submitted: 'info',
    partially_received: 'warning',
    received: 'success',
    cancelled: 'error',
  };
  return (
    <Chip label={status.replace(/_/g, ' ')} size="small" color={map[status] ?? 'default'} variant="outlined" />
  );
}

function canEditPo(po: TvPurchaseOrder) {
  return po.status === 'draft';
}

function canCancelPo(po: TvPurchaseOrder) {
  return po.status !== 'cancelled' && po.status !== 'received';
}

const TomsVendingPurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: locations = [] } = useTomVendingLocationsList();
  const { data: vendors = [] } = useTomVendingVendorsList('', false);
  const [search, setSearch] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TvPurchaseOrder | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([] as { id: string; desc: boolean }[]);
  const [tab, setTab] = useState(0);

  const { data: list = [], isLoading, refetch } = useTomVendingPurchaseOrdersList(search, showCancelled);

  const receiveAtLocations = useMemo(
    () => tomVendingReceiveAtLocations(locations, editing?.receiveLocation?.id),
    [locations, editing?.receiveLocation?.id],
  );

  const headerFieldGroups = useMemo(
    () => [
      {
        groupName: 'Purchase order',
        fields: [
          {
            label: 'Vendor',
            name: 'vendorId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: vendors.map((v) => ({ label: v.name, value: v.id })),
          },
          ...(receiveAtLocations.length === 1
            ? []
            : [
                {
                  label: 'Receive at location',
                  name: 'receiveLocationId',
                  type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
                  required: true,
                  columns: 12,
                  options: receiveAtLocations.map((l) => ({
                    label: l.name ?? `Location ${l.id}`,
                    value: l.id,
                  })),
                },
              ]),
          { label: 'PO number', name: 'poNumber', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
          {
            label: 'Notes',
            name: 'notes',
            type: GENERIC_FORM_FIELD_TYPE.TEXT,
            required: false,
            columns: 12,
          },
        ],
      },
    ],
    [vendors, receiveAtLocations],
  );

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((po: TvPurchaseOrder) => {
    setEditing(po);
    setModalOpen(true);
  }, []);

  const handleCancel = useCallback(
    async (po: TvPurchaseOrder) => {
      if (!window.confirm(`Cancel purchase order "${po.poNumber}"?`)) return;
      try {
        await cancelTomVendingPurchaseOrder(po.id);
        showSuccess('Purchase order cancelled');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to cancel');
      }
    },
    [refetch, showError, showSuccess],
  );

  const columns = useMemo<MRT_ColumnDef<TvPurchaseOrder>[]>(
    () => [
      { accessorKey: 'poNumber', header: 'PO #', size: 140 },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
        size: 160,
        Cell: ({ row }) => row.original.vendor?.name ?? '—',
      },
      {
        accessorKey: 'receiveLocation.name',
        header: 'Receive location',
        size: 160,
        Cell: ({ row }) => row.original.receiveLocation?.name ?? '—',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        Cell: ({ row }) => statusChip(row.original.status),
      },
      {
        accessorKey: 'orderedAt',
        header: 'Ordered',
        size: 110,
        Cell: ({ row }) => {
          const d = row.original.orderedAt;
          return d ? new Date(d).toLocaleDateString() : '—';
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 90,
        Cell: ({ row }) => {
          const po = row.original;
          return (
            <GenericActionPicker
              row={po}
              actions={[
                {
                  icon: GENERIC_ICON_NAME.VIEW,
                  label: 'Open',
                  onClick: (r) => navigate(`/app/toms-vending/purchasing/purchase-orders/${r.id}`),
                },
                ...(canEditPo(po)
                  ? [{ icon: GENERIC_ICON_NAME.EDIT, label: 'Edit header', onClick: (r: TvPurchaseOrder) => openEdit(r) }]
                  : []),
                ...(canCancelPo(po)
                  ? [{ icon: GENERIC_ICON_NAME.BLOCK, label: 'Cancel', onClick: (r: TvPurchaseOrder) => void handleCancel(r) }]
                  : []),
              ]}
            />
          );
        },
      },
    ],
    [handleCancel, navigate, openEdit],
  );

  const modalInitialValues = editing
    ? {
        vendorId: editing.vendor?.id ?? '',
        receiveLocationId: editing.receiveLocation?.id ?? '',
        poNumber: editing.poNumber ?? '',
        notes: editing.notes ?? '',
      }
    : {
        vendorId: '',
        receiveLocationId: receiveAtLocations[0]?.id ?? '',
        poNumber: `PO-${Date.now()}`,
        notes: '',
      };

  return (
    <>
      <GenericFormModal
        visible={modalOpen}
        onDismiss={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={async (values: Record<string, unknown>) => {
          const vendorId = Number(values.vendorId);
          const receiveLocationId = Number(
            values.receiveLocationId ?? receiveAtLocations[0]?.id ?? '',
          );
          const poNumber = String(values.poNumber ?? '').trim();
          if (!vendorId || !receiveLocationId || !poNumber) {
            showError('Vendor, location, and PO number are required');
            return;
          }
          try {
            if (editing) {
              await updateTomVendingPurchaseOrder({
                id: editing.id,
                vendorId,
                receiveLocationId,
                poNumber,
                notes: String(values.notes ?? '').trim() || null,
              });
              showSuccess('Purchase order updated');
              setModalOpen(false);
              setEditing(null);
              await refetch();
            } else {
              const po = await createTomVendingPurchaseOrder({
                vendorId,
                receiveLocationId,
                poNumber,
                notes: String(values.notes ?? '').trim() || undefined,
                lines: [],
              });
              showSuccess('Purchase order created — add lines on the detail screen');
              setModalOpen(false);
              navigate(`/app/toms-vending/purchasing/purchase-orders/${po.id}`);
            }
          } catch (e: unknown) {
            showError(e instanceof Error ? e.message : 'Failed to save');
          }
        }}
        initialValues={modalInitialValues}
        fieldGroups={headerFieldGroups}
        title={editing ? 'Edit purchase order' : 'New purchase order'}
      />

      <TitleBar
        breadcrumbs={[{ label: 'Home', href: '/app/dashboard' }, { label: 'Purchase orders' }]}
        title="Purchase orders"
        rightContent={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/toms-vending/purchasing/purchase-orders/from-photo')}
              sx={{ textTransform: 'none' }}
              title="Select vendor → photograph PO → store order in cloud"
            >
              New PO from photo
            </Button>
            <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
              + New purchase order
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Purchase orders" />
          <Tab label="Buying suggestions" />
          <Tab label="Replenishment policy" />
        </Tabs>

        {tab === 1 && (
          <BuyingSuggestionsPanel
            businessUnit="tv"
            locations={locations ?? []}
            vendors={vendors}
            fetchSuggestions={async (opts) =>
              (await queryTomVendingBuyingSuggestions(opts)) as BuyingSuggestionRow[]
            }
            createDraftPo={createTomVendingPurchaseOrder}
            poDetailPath={(id) => `/app/toms-vending/purchasing/purchase-orders/${id}`}
          />
        )}

        {tab === 2 && (
          <>
          <TvPolicyProposalsPanel
            fetchProposals={async () => (await fetchTomVendingPolicyProposals()) as TvPolicyProposal[]}
            onAccept={acceptTomVendingPolicyProposal}
            onDismiss={dismissTomVendingPolicyProposal}
          />
          <ReplenishmentPolicyPanel
            locations={locations ?? []}
            vendors={vendors}
            queryPolicies={async (locationId) =>
              (await queryTomVendingReplenishmentPolicies(locationId)) as ReplenishmentPolicyRow[]
            }
            savePolicy={saveTomVendingReplenishmentPolicy}
            searchCatalog={searchTomVendingCatalogItems}
          />
          </>
        )}

        {tab === 0 && (
        <>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search PO # or vendor…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 300 }}
          />
          <FormControlLabel
            control={
              <Switch checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} size="small" />
            }
            label="Show cancelled"
          />
        </Box>

        <InventoryAlertSettingsCard
          fetchSettings={fetchTomVendingInventorySettings}
          saveSettings={updateTomVendingInventorySettings}
          searchUsers={searchTomVendingAlertUsers}
          showCostingMethod
        />

        <MaterialReactTable
          columns={columns}
          data={list}
          enableGlobalFilter={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={list.length}
          state={{ isLoading, pagination, sorting }}
          initialState={{ density: 'compact', sorting: [{ id: 'poNumber', desc: true }] }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => navigate(`/app/toms-vending/purchasing/purchase-orders/${row.original.id}`),
            sx: { cursor: 'pointer' },
          })}
        />
        </>
        )}
      </Box>
    </>
  );
};

export default TomsVendingPurchaseOrdersPage;
